import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { Pagination } from '../../shared/components/Pagination';
import { ERole } from '../../shared/enums/ERole';
import { handleError } from '../../shared/functions/handleError';
import { usePagination } from '../../shared/hooks/usePagination';
import { IUser } from '../../shared/interfaces/IUser';
import { RootState } from '../../store/store';
import UserForm from './components/UserForm';
import UserTable from './components/UserTable';
import { AdminUserService } from './service';

export const schema = yup.object({
  role: yup.string().oneOf(Object.values(ERole)).required(),
  email: yup.string().email().required(),
  firstName: yup.string().min(1).max(50).required(),
  lastName: yup.string().min(1).max(50).required(),
  phoneNumber: yup.string().min(6).max(20).required(),
  birthday: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format')
    .required(),
  address: yup.string().min(1).max(255).required(),
  city: yup.string().min(1).max(100).required(),
  country: yup.string().min(1).max(100).required(),
  zipCode: yup.string().min(1).max(20).required(),
  language: yup.string().min(2).max(10).required(),
});

const AdminUserPage = () => {
  const { t } = useTranslation();
  const { role } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const isAdmin = role === ERole.ADMIN;
  const [isActionModalOpened, setIsActionModalOpened] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IUser>({
    resolver: yupResolver(schema),
    defaultValues: {
      role: ERole.USER,
    },
  });

  const {
    pagination,
    setTotalPages,
    setCount,
    changeFilterHandler,
    totalPages,
    page,
    handlePages,
    handleUpdateList,
  } = usePagination({
    perPage: 10,
  });
  const [users, setUsers] = useState<IUser[]>([]);
  const handleCloseActionModal = () => {
    setIsActionModalOpened(false);
    reset({
      firstName: '',
    });
  };
  const handleSubmitActionModal = async (data: IUser) => {
    const isUpdate = !!data?.id;
    const formData = new FormData();
    formData.append(
      'body',
      JSON.stringify({ ...data, role: data?.role ?? ERole.USER }),
    );

    await AdminUserService[isUpdate ? 'update' : 'create'](
      formData,
      Number(data?.id),
    )
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleCloseActionModal();
          toast.success(
            t(`toast.${isUpdate ? 'successUpdated' : 'successCreated'}`, {
              name: response?.displayName,
            }),
          );
        }
      })
      .catch(handleError);
  };
  useEffect(() => {
    AdminUserService.getAll(pagination)
      .then((response) => {
        setUsers(response?.data);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
      })
      .catch(handleError);
  }, [pagination, setCount, setTotalPages]);
  return (
    <div>
      <div className="mb-3">
        <div className="w-100 d-flex justify-content-between align-items-center">
          {isAdmin && (
            <Button
              variant={'success'}
              className={'d-flex align-items-center'}
              onClick={() => setIsActionModalOpened(true)}
            >
              <PlusLg className={'me-1'} />
              {t('button.create')}
            </Button>
          )}
          <div className={'d-flex align-items-center'}>
            <Form.Control
              type="text"
              className={'w-50 mx-1'}
              onChange={(e) => changeFilterHandler('email', e.target.value)}
              placeholder={t('general.searchByEmail')}
            />
            <Form.Control
              type="text"
              className={'w-50'}
              onChange={(e) => changeFilterHandler('firstName', e.target.value)}
              placeholder={t('general.searchByName')}
            />
          </div>
        </div>
      </div>
      <UserTable
        users={users}
        isAdmin={isAdmin}
        reset={reset}
        setIsActionModalOpened={setIsActionModalOpened}
        handleUpdateList={handleUpdateList}
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        handlePagination={handlePages}
        changeFilterHandler={changeFilterHandler}
      />
      <Modal
        show={isActionModalOpened}
        onHide={handleCloseActionModal}
        size={'lg'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t(watch('id') ? 'general.update' : 'general.create')}{' '}
            {t('general.user')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <UserForm
            register={register}
            errors={errors}
            isUpdate={!!watch('id')}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseActionModal}>
            {t('button.close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(handleSubmitActionModal)}
          >
            {t(watch('id') ? 'button.update' : 'button.create')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminUserPage;
